/// <amd-module name="TFS/Admin/Common/Components/ResourceUsage" />
import * as React from 'react';
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { getResourceUsageClient } from "../RestClient/ResourceUsage";
import { IVssPageContext } from "VSS/Platform/Context";
import { Usage } from "../Models/ResourceUsage";
import {
  Table,
  renderSimpleCell,
  TableColumnLayout,
  ITableColumn,
} from "VSSUI/Table";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { ProgressBar } from "VSSUI/ProgressBar";
 
interface IProps {
  source: string;
  projectId: string;
  resourceUsageName: string;
}
 
interface IState {
  limits: { [key: string]: Usage };
  projectId: string;
}
 
interface IContext {
  pageContext: IVssPageContext;
}
 
export class ResourceUsageComponent extends VssComponent<IProps, IState, IContext> {
  public constructor(props: IProps, context: IVssComponentContext) {
    super(props, context);
   
    this.state = {
      limits: {},
      projectId: props.projectId,
    };

    // Bind 'this' to the methods
    this.getTeamProjectCollectionLimits = this.getTeamProjectCollectionLimits.bind(this);
    this.getTeamCollectionLimits = this.getTeamCollectionLimits.bind(this);
  }
 
  public async componentDidMount(): Promise<void> {
    const methodMap: { [key: string]: (pageContext: IVssPageContext) => Promise<{ [key: string]: Usage }> } = {
      'ProjectOverview': this.getTeamProjectCollectionLimits,
      'OrganizationOverview': this.getTeamCollectionLimits
    };
  
    const methodToCall = methodMap[this.props.source as keyof typeof methodMap];
  
    if (methodToCall) {
      try {
        const limits = await methodToCall(this.context.pageContext);
        this.setState({ limits });
      } catch (error) {
        console.error("exception occured"+error);
        this.setState({ limits: {} });
      }
    }
  }

  private getProgressBarColor(currentValue: number, maxValue: number): string {
    const percentage = (currentValue / maxValue) * 100;
    if (percentage >= 90) return 'red-bar';
    if (percentage >= 75) return 'orange-bar';
    return 'green-bar';
  }
 
  public render(): JSX.Element {
    const { limits } = this.state;
    const data = Object.entries(this.state.limits).map(([key, value]) => ({
      type: key,
      usage: value.count !== undefined && value.limit !== undefined ? `${value.count},${value.limit}` : '0,0',
    }));

    return (
      <div>
        <div className="title-m service-title">{this.props.resourceUsageName}</div>
        <Table
          ariaLabel="Basic Table"
          columns={[
            {
              columnLayout: TableColumnLayout.singleLinePrefix,
              id: "type",
              name: "Type",
              readonly: true,
              renderCell: renderSimpleCell,
              width: 240,
            },
            {
              id: "usage",
              name: "Usage",
              readonly: true,
              width: 380,
              renderCell: (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<any>, item: any) => {
                const [count, limit] = item.usage.split(',').map(Number);
                return (
                  <div className="flex-row flex-center data-container">
                    <ProgressBar
                      className="progress-bar-container"
                      currentValue={count}
                      maxValue={limit}
                      progressBarClassName={this.getProgressBarColor(count, limit)}
                    />
                    <span className="usage-text">{`${count}/${limit}`}</span>
                  </div>
                );
              },
            },
          ]}
          itemProvider={new ArrayItemProvider(data)}
        />
      </div>
    );
  }
 
  private async getTeamProjectCollectionLimits(pageContext: IVssPageContext): Promise<{ [key: string]: Usage }> {
    const resourceUsageClient = getResourceUsageClient(pageContext);
    const limits = await resourceUsageClient.getProjectLimit(this.props.projectId);
    return limits;
  }
 
  private async getTeamCollectionLimits(pageContext: IVssPageContext): Promise<{ [key: string]: Usage }> {
    const resourceUsageClient = getResourceUsageClient(pageContext);
    const limits = await resourceUsageClient.getLimits();
    return limits;
  }
}